/**
 * Application Constants
 * Centralized constants to replace magic numbers throughout the application
 */

// ===== TIMING CONSTANTS =====

/** Notification display duration in milliseconds */
export const NOTIFICATION_AUTO_HIDE_DURATION = 5000; // 5 seconds

/** Notification auto-close timeout (aligned with Mantine default) */
export const NOTIFICATION_AUTO_CLOSE_DEFAULT = 4000; // 4 seconds

/** Performance monitoring interval in milliseconds */
export const PERFORMANCE_MONITOR_INTERVAL = 5000; // 5 seconds

/** Memory check interval for performance monitoring */
export const MEMORY_CHECK_INTERVAL = 5000; // 5 seconds

/** API request timeout in milliseconds */
export const API_REQUEST_TIMEOUT = 30000; // 30 seconds

/** Debounce delay for search inputs in milliseconds */
export const SEARCH_DEBOUNCE_DELAY = 300; // 300ms

/** Debounce delay before refetching the public shared-map LIST + MAP on a viewer-filter change */
export const SHARE_VIEWER_FILTER_DEBOUNCE_MS = 300; // 300ms

/** Auto-save delay for form data in milliseconds */
export const AUTOSAVE_DELAY = 2000; // 2 seconds

// ===== INTERACTION TIMING CONSTANTS =====

/** Single-click delay to allow double-click detection in milliseconds */
export const INTERACTION_SINGLE_CLICK_DELAY = 200; // 200ms

/** Long-press duration for mobile context menu in milliseconds */
export const INTERACTION_LONG_PRESS_DURATION = 500; // 500ms

/** Double-click protection timeout to prevent duplicate actions in milliseconds */
export const INTERACTION_DOUBLE_CLICK_PROTECTION = 250; // 250ms

/**
 * Maximum time between mousedown and mouseup to be considered a click (not drag)
 * If mousedown→mouseup duration > this value, it's treated as drag attempt
 * Used to prevent adding line points when user holds mouse button to drag
 */
export const CLICK_TIME_THRESHOLD_MS = 200; // 200ms

/**
 * Duration to keep double-click zoom disabled after touch on geometry (ms)
 * Prevents MapLibre's double-tap zoom when entering edit mode on mobile
 */
export const DOUBLE_TAP_ZOOM_PROTECTION_DURATION = 500; // 500ms

/** Maximum time between taps to be considered double-tap in milliseconds */
export const INTERACTION_DOUBLE_TAP_THRESHOLD = 300; // 300ms

/** Maximum touch duration to be considered a tap (not long-press) in milliseconds */
export const INTERACTION_TAP_MAX_DURATION = 300; // 300ms

/** Touch movement threshold to cancel long-press in pixels */
export const INTERACTION_MOVE_THRESHOLD = 10; // 10px

/** Duration to suppress map interactions after panel click-outside close (ms) */
export const INTERACTION_SUPPRESSION_DURATION = 300; // 300ms

// ===== UI CONSTANTS =====

/** Maximum number of suggestions to display */
export const MAX_SUGGESTIONS_COUNT = 5;

/** Maximum number of recent tasks to keep for smart defaults */
export const MAX_RECENT_TASKS = 10;

// ===== NOTIFICATION SYSTEM CONSTANTS =====

/** Maximum number of visible notifications at once */
export const MAX_VISIBLE_NOTIFICATIONS = 5;

/** Notification icon size in pixels */
export const NOTIFICATION_ICON_SIZE = 20;

/** Vertical overlap offset between stacked notifications in pixels */
export const NOTIFICATION_OVERLAP_OFFSET = 10;

/** Enable duplicate notification prevention */
export const NOTIFICATION_DEDUPE_ENABLED = true;

/** Notification color palette (Mantine semantic colors) */
export const NOTIFICATION_COLOR_SUCCESS = 'green';
export const NOTIFICATION_COLOR_ERROR = 'red';
export const NOTIFICATION_COLOR_WARNING = 'orange';
export const NOTIFICATION_COLOR_INFO = 'indigo';

/** Duration of notification shake animation in milliseconds */
export const NOTIFICATION_SHAKE_DURATION_MS = 400;

/** Shake animation horizontal offset in pixels */
export const NOTIFICATION_SHAKE_OFFSET_PX = 4;

/** Maximum character length for text truncation */
export const MAX_TEXT_TRUNCATION = 40;

/** Maximum height for virtualized list items */
export const LIST_ITEM_HEIGHT = 100; // pixels

/** Minimum touch target size for mobile (pixels) */
export const MIN_TOUCH_TARGET = 44;

/** Mobile minimum touch target size (pixels) */
export const MOBILE_MIN_TOUCH_TARGET = 48;

// ===== TASK DEFAULTS =====

/** Default due date offset for priority levels (in days) */
export const PRIORITY_DUE_DATE_OFFSETS = {
  Low: 14, // 2 weeks
  Medium: 7, // 1 week
  High: 3, // 3 days
  Urgent: 1, // 1 day
} as const;

/** Priority to numeric mapping for calculations */
export const PRIORITY_NUMERIC_VALUES = {
  Low: 1,
  Medium: 2,
  High: 3,
  Urgent: 4,
} as const;

/** Reverse mapping from numeric to priority */
export const NUMERIC_TO_PRIORITY = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Urgent',
} as const;

// ===== DATA CONVERSION =====

/** Milliseconds in various time units */
export const TIME_UNITS = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
} as const;

// ===== MAP CONSTANTS =====

/** Default map center coordinates (Warsaw, Poland) */
export const DEFAULT_MAP_CENTER = { lat: 52.2297, lng: 21.0122 } as const;

/** Default map zoom level */
export const DEFAULT_MAP_ZOOM = 6;

/** Cluster radius for map markers */
export const MARKER_CLUSTER_RADIUS = 50; // pixels

// ===== ANCHOR PIN CONSTANTS =====

/** Minimum anchor pin size in pixels (at low zoom) */
export const ANCHOR_MIN_SIZE_PX = 32;

/** Maximum anchor pin size in pixels (at high zoom) */
export const ANCHOR_MAX_SIZE_PX = 56;

/** Zoom multiplier for anchor pin responsive sizing */
export const ANCHOR_ZOOM_MULTIPLIER = 3;

// ===== VALIDATION CONSTANTS =====

/** Minimum task title length */
export const MIN_TASK_TITLE_LENGTH = 3;

/** Maximum task title length */
export const MAX_TASK_TITLE_LENGTH = 100;

/** Maximum task description length */
export const MAX_TASK_DESCRIPTION_LENGTH = 1000;

// ===== PERFORMANCE =====

/** Bundle size target in KB (gzipped) */
export const BUNDLE_SIZE_TARGET = 250;

/** Component render time target in milliseconds */
export const COMPONENT_RENDER_TIME_TARGET = 16;

/** Maximum allowed memory usage in MB */
export const MAX_MEMORY_USAGE = 100;

// ===== ACCESSIBILITY =====

/** Focus outline width in pixels */
export const FOCUS_OUTLINE_WIDTH = 2;

/** Focus outline offset in pixels */
export const FOCUS_OUTLINE_OFFSET = 2;

// ===== ERROR HANDLING =====

/** Maximum retry attempts for failed requests */
export const MAX_RETRY_ATTEMPTS = 3;

/** Base delay for exponential backoff in milliseconds */
export const RETRY_BASE_DELAY = 1000;

// ===== RE-EXPORTS FROM OTHER CONSTANT FILES =====

// Export UI-related constants
export * from './ui';

// Export browser tabs UI constants
export * from './uiTabs';

// Export legacy TASK_CONSTANTS for backward compatibility
export const TASK_CONSTANTS = {
  NOTIFICATION_AUTO_HIDE_DURATION,
  MAX_SUGGESTIONS_COUNT,
  MAX_RECENT_TASKS,
  MAX_TEXT_TRUNCATION,
  MIN_TOUCH_TARGET,
  API_REQUEST_TIMEOUT,
  SEARCH_DEBOUNCE_DELAY,
  SHARE_VIEWER_FILTER_DEBOUNCE_MS,
  AUTOSAVE_DELAY,
};

// ===== GEOMETRY RENDERING CONSTANTS =====
// Export all geometry rendering constants from dedicated file
export * from './geometryRendering';

// ===== GEOMETRY LOGIC CONSTANTS =====
// Export all geometry business logic constants from dedicated file
export * from './geometry';

// ===== GEOLOCATION CONSTANTS =====
// Export all geolocation/GPS configuration constants from dedicated file
export * from './geolocation';

// ===== USER LOCATION CONSTANTS =====
// Export live-location marker + broadcast constants from dedicated file
export * from './userLocation';

// ===== DEVICE DETECTION CONSTANTS =====
// Export all device detection constants (layout mode, input mode, orientation, pointer)
export * from './deviceDetection';

// ===== API CONSTANTS =====
// Export API endpoint and configuration constants
export * from './api';

// ===== WORKSPACE CONSTANTS =====
// Export workspace management constants
export * from './workspace';

// ===== STATUS BADGE COLORS =====
// Export status badge color constants for StatusBadge component
export * from './statusColors';

// ===== APPSHELL LAYOUT CONSTANTS =====
// Export AppShell layout dimensions for sidebar, topbar, and navigation
export * from './appShell';

// ===== ROLE & AVATAR CONSTANTS =====
// Export workspace role configuration, avatar colors, and utilities
export * from './roles';

// ===== MAP SETUP CONSTANTS =====
// Export per-workspace map configurator (WorkspaceMapConfigPage) constants
export * from './mapSetup';

// ===== COUNTRY CONSTANTS =====
// Export the shared ISO country-code constants (Poland special-casing)
export * from './country';

// ===== MARKETING CONSTANTS =====
// Export the public marketing website URL constants
export * from './marketing';
